<script setup lang="ts">
import { ref, computed } from "vue"
import type { fetchedUser } from "@/types"
import type { AcTrackedPotential } from "@/types/crm"
import type { Ref } from 'vue'
import type { ColorMetaField } from "@/types/metas"
import { useGetFilteredDocs, useGetDocs, postData } from "@/mixins/modularFirebaseOperations"
import { formatUser, modalClosed } from "@/mixins/visualFormatters"
import { useStore } from "vuex"
import { useRouter } from "vue-router"
import AcDataTable from "@/components/reusable/acDataTable.vue"
import StarDisplay from "@/components/reusable/starDisplay.vue"
import ModernStatusChip from "@/components/reusable/modernStatusChip.vue"
import FilterPopup from "@/components/reusable/filterPopup.vue"
import CrmPotentialCard from "@/components/crm-components/potentialCard.vue"
import AcMediumModal from "@/components/reusable/teleports/acMediumModal.vue"
import AddTrackedPotentialForm from "@/components/crm-components/addTrackedPotentialForm.vue"
import SkeletonTable from "@/components/reusable/skeletonTable.vue"

const isAddingPotential = ref(false)
const router = useRouter()
const { docs, isLoadingDocs } = useGetFilteredDocs("crm/tracked/list", { status: "ACTIVE" })
const potentials = docs as Ref<AcTrackedPotential[]>

// Get job categories for filtering
const { docs: categoryDocs } =  useGetDocs("crm/jobCategories/list")
const jobCategories = categoryDocs as Ref<ColorMetaField[]>

// Search and filter state
const searchQuery = ref('')
const selectedCategoryFilter = ref<ColorMetaField | null>(null)
const isShowingCategoryFilter = ref(false)
const isShowingAssignedToMe = ref(false)
const store = useStore()
const userProfile = computed(() => store.getters['user/userProfile'])
const prefView = computed(() => userProfile.value?.preferences?.crmView || 'list')

// Sorting state
const sortColumn = ref('')
const sortDirection = ref<'asc' | 'desc'>('asc')

const changeUserView = () => {

    if(prefView.value === 'list'){
        store.dispatch('user/changeCrmPreference', { key: 'crmView', value: 'card' })
    } else {
        store.dispatch('user/changeCrmPreference', { key: 'crmView', value: 'list' })
    }
}

// Handle category filter selection
const handleCategorySelected = (category: ColorMetaField | undefined) => {
    selectedCategoryFilter.value = category || null
}

const handleCategoryFilterClosed = () => {
    isShowingCategoryFilter.value = false
}

const user = userProfile.value

const handleTrackedPotentialAdded = async (potential: AcTrackedPotential) => {
    try{
        const finalPotential: AcTrackedPotential = {
            ...potential,
            createdAt: new Date(),
            updatedAt: {
                date: new Date(),
                user: formatUser(user),
            },
            addedBy: formatUser(user),
            status: "ACTIVE",
            assignedTo: [formatUser(user)],
            comments: [],
            milestones: [],
            trackedBy: formatUser(user),
            trackedAt: new Date(),
            priority: potential.priority,
            
        }
        
        const newId = await postData(finalPotential, "crm/tracked/list")
        router.push(`/crm/tracked-detail/${newId}`)
    } catch (error) {
        console.error(error)
    } finally {
        isAddingPotential.value = false
        modalClosed()
    }
}

// Filtered potentials based on search and category
const filteredPotentials = computed(() => {
    let filtered = potentials.value

    // Filter by search query
    if (searchQuery.value.trim()) {
        const query = searchQuery.value.toLowerCase().trim()
        filtered = filtered.filter(potential => 
            potential.title.toLowerCase().includes(query) ||
            potential.description?.toLowerCase().includes(query) ||
            potential.potentialCategories.some((cat: ColorMetaField) => cat.name?.toLowerCase().includes(query))
        )
    }

    // Filter by selected category
    if (selectedCategoryFilter.value) {
        filtered = filtered.filter(potential => 
            potential.potentialCategories.some((cat: ColorMetaField) => cat.id === selectedCategoryFilter.value?.id)
        )
    }

    // Filter by assigned to me
    if (isShowingAssignedToMe.value && user?.userId) {
        filtered = filtered.filter(potential => 
            potential.assignedTo?.some((assignedUser: fetchedUser) => assignedUser.id === user.userId)
        )
    }

    return filtered
})

// Sorted and filtered potentials
const sortedAndFilteredPotentials = computed(() => {
    let result = [...filteredPotentials.value]

    // Apply sorting if a sort column is selected
    if (sortColumn.value) {
        const column = dataCols.value.find((col: any) => col.key === sortColumn.value)
        
        result.sort((a, b) => {
            // Use custom sort function if available
            if (column?.sortFunction) {
                const tableDataA = {
                    id: a.id,
                    title: a.title,
                    categories: a.potentialCategories,
                    potentialRating: a.potentialRating,
                    priority: {
                        displayName: priorityData[a.priority as keyof typeof priorityData]?.displayName || a.priority,
                        sortValue: priorityData[a.priority as keyof typeof priorityData]?.sortValue ?? 999,
                        rawValue: a.priority
                    },
                    trackedAt: {
                        displayValue: (a.trackedAt instanceof Date ? a.trackedAt : (a.trackedAt as any).toDate()).toLocaleDateString("tr-TR"),
                        sortValue: (a.trackedAt instanceof Date ? a.trackedAt : (a.trackedAt as any).toDate()).getTime()
                    },
                    actions: a
                }
                
                const tableDataB = {
                    id: b.id,
                    title: b.title,
                    categories: b.potentialCategories,
                    potentialRating: b.potentialRating,
                    priority: {
                        displayName: priorityData[b.priority as keyof typeof priorityData]?.displayName || b.priority,
                        sortValue: priorityData[b.priority as keyof typeof priorityData]?.sortValue ?? 999,
                        rawValue: b.priority
                    },
                    trackedAt: {
                        displayValue: (b.trackedAt instanceof Date ? b.trackedAt : (b.trackedAt as any).toDate()).toLocaleDateString("tr-TR"),
                        sortValue: (b.trackedAt instanceof Date ? b.trackedAt : (b.trackedAt as any).toDate()).getTime()
                    },
                    actions: b
                }
                
                return column.sortFunction(tableDataA, tableDataB)
            }
            
            // Default sorting for other columns
            const aValue = a[sortColumn.value as keyof typeof a]
            const bValue = b[sortColumn.value as keyof typeof b]
            
            // Handle undefined/null values
            if (aValue == null && bValue == null) return 0
            if (aValue == null) return -1
            if (bValue == null) return 1
            
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                return aValue.localeCompare(bValue)
            }
            
            if (aValue < bValue) return -1
            if (aValue > bValue) return 1
            return 0
        })
        
        // Apply sort direction
        if (sortDirection.value === 'desc') {
            result.reverse()
        }
    }

    return result
})

const priorityData = {
    "LOW": {
        "displayName": "Düşük",
        "sortValue": 0
    },
    "STANDARD": {
        "displayName": "Standart",
        "sortValue": 1
    },
    "HIGH": {
        "displayName": "Yüksek",
        "sortValue": 2
    }
}

const tableData = computed(() => {
    return sortedAndFilteredPotentials.value.map(doc => {
        const trackedAtDate = doc.trackedAt instanceof Date ? doc.trackedAt : (doc.trackedAt as any).toDate()
        const priorityInfo = priorityData[doc.priority as keyof typeof priorityData]
        return {
            id: doc.id, // Include the original id for navigation
            title: doc.title,
            categories: doc.potentialCategories,
            potentialRating: doc.potentialRating,
            priority: {
                displayName: priorityInfo?.displayName || doc.priority,
                sortValue: priorityInfo?.sortValue ?? 999, // Use high number for unknown priorities
                rawValue: doc.priority
            },
            trackedAt: {
                displayValue: trackedAtDate.toLocaleDateString("tr-TR"),
                sortValue: trackedAtDate.getTime() // Use timestamp for sorting
            },
            actions: doc, // Include the full potential object for actions
        }
    })
})

const dataCols = computed(() => [
    {
        title: sortColumn.value === "title" 
            ? `Başlık ${sortDirection.value === 'asc' ? '▲' : '▼'}` 
            : "Başlık",
        key: "title",
        width: "20%",
        isSortable: true
    },
    {
        title: "Kategoriler",
        key: "categories",
        width: "20%",
        isSortable: false,
    },
    {
        title: sortColumn.value === "trackedAt" 
            ? `Takip Tarihi ${sortDirection.value === 'asc' ? '▲' : '▼'}` 
            : "Takip Tarihi",
        key: "trackedAt",
        width: "20%",
        isSortable: true,
        sortFunction: (a: any, b: any) => {
            const aValue = a.trackedAt?.sortValue ?? 0
            const bValue = b.trackedAt?.sortValue ?? 0
            return aValue - bValue
        }
    },
    {
        title: sortColumn.value === "potentialRating" 
            ? `Fırsat Düzeyi ${sortDirection.value === 'asc' ? '▲' : '▼'}` 
            : "Fırsat Düzeyi",
        key: "potentialRating",
        width: "20%",
        isSortable: true,
        sortFunction: (a: any, b: any) => {
            const aValue = a.potentialRating ?? 0
            const bValue = b.potentialRating ?? 0
            return aValue - bValue
        }
    },
    {
        title: sortColumn.value === "priority" 
            ? `Takip Önceliği ${sortDirection.value === 'asc' ? '▲' : '▼'}` 
            : "Takip Önceliği",
        key: "priority",
        width: "10%",
        isSortable: true,
        sortFunction: (a: any, b: any) => {
            const aValue = a.priority?.sortValue ?? 999
            const bValue = b.priority?.sortValue ?? 999
            return aValue - bValue
        }
    },

])

const navigateToPotential = (item: any) => {
    router.push(`/crm/tracked-detail/${item.id}`)
}


const handleColumnSort = (columnKey: string) => {
    if (sortColumn.value === columnKey) {
        // If clicking the same column, toggle direction
        sortDirection.value = sortDirection.value === 'asc' ? 'desc' : 'asc'
    } else {
        // If clicking a new column, set it as sort column with ascending direction
        sortColumn.value = columnKey
        sortDirection.value = 'asc'
    }
}

</script>

<template>
    <main class="main-container content-start mt-8">
        <h2 class="ac-large-title col-start-1 col-end-13 mb-0">Takip Listesi</h2>
        <div class="actions-row col-start-1 col-end-13 flex items-center justify-between gap-4">
            <!-- Search and filters on the left -->
            <div class="flex items-center gap-4">
                <!-- Search input -->
                <div class="relative">
                    <img 
                        src="../../assets/icons/search.png" 
                        alt="Ara" 
                        class="absolute left-3 top-1/2 transform -translate-y-1/2 w-6 h-6 opacity-50"
                    />
                    <input 
                        v-model="searchQuery"
                        type="text" 
                        placeholder="Fırsatlarda ara..."
                        class="px-12 py-3 opacity-50 text-2xl border border-gray-300 rounded-lg focus:outline-none  min-w-[250px]"
                        :class="{ '!opacity-100': searchQuery !== '' }"
                    />

                </div>
                <!-- Category filter -->
                <div class="relative">
                    <button 
                        @click.stop="isShowingCategoryFilter = true"
                        class="ac-add-button"
                        :class="{ 'stateful-background': selectedCategoryFilter }"
                    >
                        <img 
                            :class="{'filter-white': !selectedCategoryFilter}" 
                            src="../../assets/icons/filter.png" 
                            alt="Filtrele"
                        />
                        <span v-if="selectedCategoryFilter">{{ selectedCategoryFilter.name }}</span>
                        <span v-else>Kategori</span>
                    </button>
                    
                    <filter-popup 
                        @filterClosed="handleCategoryFilterClosed"
                        @item-selected="handleCategorySelected" 
                        class="absolute w-max" 
                        v-if="isShowingCategoryFilter" 
                        :itemList="jobCategories" 
                        displayed-attribute="name" />
                </div>
                <!-- Assigned to me filter -->
                <button 
                    @click="isShowingAssignedToMe = !isShowingAssignedToMe"
                    class="ac-add-button"
                    :class="{ 'stateful-background': isShowingAssignedToMe }"
                >
                    <img 
                        :class="{'filter-white': !isShowingAssignedToMe}" 
                        src="../../assets/icons/filter.png" 
                        alt="Bana Atananları Listele"
                    />
                    <span v-if="isShowingAssignedToMe">Bana Atananlar</span>
                    <span v-else>Bana Atananları Listele</span>
                </button>
                <div @click="changeUserView()" class="ac-add-button">
                    <img class="filter-main" v-if="prefView === 'list'" src="../../assets/icons/gridView.png" alt="Filtrele">
                    <img class="filter-main" v-if="prefView === 'card'" src="../../assets/icons/listView.png" alt="Filtrele">
                    <span v-if="prefView === 'card'" >Liste Görünümü</span>
                    <span v-if="prefView === 'list'">Kart Görünümü</span>
                </div>
            </div>
            
            <!-- Add button on the right -->
            <button @click="isAddingPotential = true" class="ac-add-button">
                <img class="mr-4 filter-main" width="20" src="../../assets/icons/add.png" alt="" />
                Yeni Ekle
            </button>
        </div>
        <skeleton-table class="col-start-1 col-end-13 w-full" v-if="isLoadingDocs" width="100" rows="20" />
        <ac-data-table
        @row-clicked="navigateToPotential($event)"
        v-else-if="!isLoadingDocs && prefView === 'list'"
        @column-sorted="handleColumnSort"
        class="w-full col-start-1 col-end-13" 
        :data="tableData" :columns="dataCols">
            <template #cell-potentialRating="{ value }">
                <star-display :picked-quantity="value" />
            </template>
            <template #cell-categories="{ value }">
                <div class="flex gap-2 overflow-x-scroll max-w-[20vw] overflow-x-bars-hidden">
                    <modern-status-chip 
                        v-for="category in value" 
                        :key="category.id"
                        :text="category.name"
                        :bgColors="{ [category.id]: category.color }"
                        :value="category.id"
                        class="text-sm"
                    />
                </div>
            </template>
            <template #cell-priority="{ value }">
                <span class="font-medium">{{ value.displayName }}</span>
            </template>
            <template #cell-trackedAt="{ value }">
                <span>{{ value.displayValue }}</span>
            </template>
        </ac-data-table>
        <div v-else-if="!isLoadingDocs && prefView === 'card'" class="col-start-1 col-end-13 grid grid-cols-4 gap-8">
            <crm-potential-card v-for="potential in sortedAndFilteredPotentials" :key="potential.id" :potential="potential" @click="navigateToPotential(potential)" />
        </div>
        <teleport to="body"> 
            <transition
                appear
                type="animation"
                enter-active-class="animate__animated animate__fadeIn animate__fast"
                leave-active-class="animate__animated animate__fadeOut animate__faster"
            >
                <ac-medium-modal class="lg:!top-44 overflow-y-scroll" @modal-closed="isAddingPotential = false" v-if="isAddingPotential" width="70vw" height="80vh" title="Takip Listesine Ekle">
                    <add-tracked-potential-form @potential-added="handleTrackedPotentialAdded" @cancel="isAddingPotential = false" />
                </ac-medium-modal>
            </transition>
        </teleport>
    </main>
</template>

<style lang="scss">

@import "../../styles/global/buttons.scss";

</style>